<template>
  <v-dialog :value="value" fullscreen persistent scrollable>
    <CustomizeDayDialog :is-selected-day-active="findDayInSchedule(selectedDayId) !== -1"
                        :selected-day="selectedDay"
                        :selected-day-id="selectedDayId"
                        :selected-day-name="selectedDayName"
                        v-model="isCustomizeDayDialogVisible"
                        @input="clearSelectedDay()"
                        @onDayCustomized="addOrRemoveDayFromSequenceValidHours" />

    <v-card color="kbLight">
      <v-toolbar color="kbLight" flat max-height="64px">
        <span class="text-h5 kbDarkBlueText--text text-capitalize font-weight-medium pl-4">
          {{ method === 'edit' ? $t('general.editSchedule') : $t('general.newSchedule') }}
        </span>
        <v-spacer></v-spacer>
        <v-btn color="kbDarkBlueText" icon @click="toggleModal()">
          <v-icon>clear</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider/>

      <v-card-text v-if="loadingSchedule">
        <LoadingComponent :message="$t('general.loadingSchedule')" />
      </v-card-text>

      <v-card-text v-else-if="method === 'edit' &&
                              !loadingSchedule &&
                              (!selectedSchedule || !Object.keys(selectedSchedule).length)">
        <NotFound :message="$t('general.scheduleNotFound')" />
      </v-card-text>

      <v-card-text v-else>
        <v-form ref="form" @submit.prevent="">
          <v-row>
            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text d-block text-h6 pl-2">
                {{ $t('general.addScheduleName') }}
              </span>
              <v-text-field background-color="kbWhite"
                            color="kbDarkBlueText"
                            :label="$t('general.scheduleName')"
                            :rules="[rules.required]"
                            solo
                            v-model="schedule.name" />
            </v-col>

            <v-col cols="12">
              <v-divider />
            </v-col>

            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text d-block text-h6 pl-2">
                {{ $t('general.addScheduleObservations') }}
              </span>
              <v-textarea background-color="kbWhite"
                          color="kbDarkBlueText"
                          :label="$t('general.scheduleObservations')"
                          solo
                          v-model="schedule.observation" />
            </v-col>

            <v-col cols="12">
              <span class="kbDarkBlueText--text d-block text-h6 mb-3 pl-2">
                {{ $t('general.customizeScheduleDays') }}
              </span>
              <div class="d-flex justify-space-between align-center">
                <div style="flex-basis: calc(100% / 7)"
                     class="pa-2"
                     v-for="day in daysOfTheWeek"
                     :key="day.id">
                  <ScheduleDayCard :day-name="day.name"
                                   :day="schedule.dayAndHours[findDayInSchedule(day.id)] || {}"
                                   :is-day-enabled="findDayInSchedule(day.id) !== -1"
                                   @click.native="openCustomizeDayDialog(day.id, day.name)" />
                </div>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="d-flex justify-end">
        <v-btn
          color="kbBlue"
          class="mr-2"
          outlined
          @click="toggleModal()">
          {{ $t('general.cancel') }}
        </v-btn>
        <v-btn
          color="kbBlue"
          dark
          :loading="loadingSubmit"
          @click="submit()">
          {{ method === 'edit' ? $t('general.edit') : $t('general.create') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CustomizeDayDialog from '@/pages/App/Sequences/Schedules/dialogs/CustomizeDay'
import LoadingComponent from '@/components/ui/LoadingComponent'
import ScheduleDayCard from '@/pages/App/Sequences/Schedules/ScheduleDayCard'
import NotFound from '@/components/ui/NotFound'
import { mapState } from 'vuex'
import notifications from '@/libs/notifications'
import rules from '@/libs/rules'
export default {
  components: { CustomizeDayDialog, NotFound, LoadingComponent, ScheduleDayCard },
  computed: {
    ...mapState({
      selectedSchedule: state => state.sequences.selectedSchedule
    })
  },
  data () {
    return {
      daysOfTheWeek: [
        { name: this.$t('general.monday'), id: 1 },
        { name: this.$t('general.tuesday'), id: 2 },
        { name: this.$t('general.wednesday'), id: 3 },
        { name: this.$t('general.thursday'), id: 4 },
        { name: this.$t('general.friday'), id: 5 },
        { name: this.$t('general.saturday'), id: 6 },
        { name: this.$t('general.sunday'), id: 7 }
      ],
      isCustomizeDayDialogVisible: false,
      loadingSchedule: false,
      loadingSubmit: false,
      rules,
      schedule: {
        name: '',
        observation: '',
        dayAndHours: [
          { dayOfTheWeek: 1, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 2, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 3, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 4, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 5, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 6, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 7, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 }
        ]
      },
      selectedDay: {},
      selectedDayId: 0,
      selectedDayName: ''
    }
  },
  methods: {
    addOrRemoveDayFromSequenceValidHours ({ day, isDayActive }) {
      if (!this.schedule.dayAndHours || !Array.isArray(this.schedule.dayAndHours)) {
        this.schedule.dayAndHours = []
      }
      const foundDayIndex = this.schedule.dayAndHours
        .map(item => item.dayOfTheWeek)
        .indexOf(day.dayOfTheWeek)
      if (foundDayIndex !== -1) {
        if (isDayActive) {
          this.schedule.dayAndHours[foundDayIndex] = day
        } else {
          this.schedule.dayAndHours.splice(foundDayIndex, 1)
        }
      } else {
        if (isDayActive) {
          this.schedule.dayAndHours.push(day)
        }
      }
    },
    clearSelectedDay () {
      this.selectedDay = {}
      this.selectedDayId = 0
      this.selectedDayName = ''
    },
    clearSchedule () {
      this.schedule = {
        name: '',
        observation: '',
        dayAndHours: [
          { dayOfTheWeek: 1, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 2, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 3, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 4, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 5, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 6, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 },
          { dayOfTheWeek: 7, startAtHour: 0, startAtMinute: 0, finishAtHour: 23, finishAtMinute: 59 }
        ]
      }
      this.$store.commit('SET_SEQUENCES_SELECTED_SCHEDULE', {})
    },
    async createSchedule () {
      if (this.loadingSubmit) return
      if (!this.$refs.form.validate()) return
      try {
        this.loadingSubmit = true
        await this.$store.dispatch(
          'createSequencesSchedule', {
            appId: this.$store.getters.appUuid,
            data: this.schedule
          }
        )
        this.loadingSubmit = false
        notifications.success(this.$t('general.scheduleSuccessfullyCreated'))
        this.getSchedules()
        this.toggleModal()
      } catch (e) {
        this.loadingSubmit = false
        notifications.error(e)
      }
    },
    findDayInSchedule (dayId) {
      if (!this.schedule.dayAndHours ||
          !Array.isArray(this.schedule.dayAndHours) ||
          !this.schedule.dayAndHours.length) {
        return -1
      }
      return this.schedule.dayAndHours.map(item => item.dayOfTheWeek).indexOf(dayId)
    },
    async getSchedule () {
      try {
        this.loadingSchedule = true
        await this.$store.dispatch(
          'getSequencesSchedule', {
            appId: this.$store.getters.appUuid,
            scheduleId: this.selectedScheduleId
          }
        )
        this.setLocalSchedule()
        this.loadingSchedule = false
      } catch (e) {
        this.loadingSchedule = false
        notifications.error(e)
      }
    },
    getSchedules () {
      this.$emit('getSchedules')
    },
    openCustomizeDayDialog (dayId, dayName) {
      const foundDayIndex = this.findDayInSchedule(dayId)
      if (foundDayIndex !== -1) {
        this.selectedDay = { ...this.schedule.dayAndHours[foundDayIndex] }
      } else {
        this.selectedDay = {}
      }
      this.selectedDayName = dayName
      this.selectedDayId = dayId
      this.isCustomizeDayDialogVisible = true
    },
    setLocalSchedule () {
      this.schedule = { ...this.selectedSchedule }
    },
    submit () {
      this.method === 'edit' ? this.updateSchedule() : this.createSchedule()
    },
    toggleModal () {
      this.$emit('input', false)
      this.clearSchedule()
    },
    async updateSchedule () {
      if (this.loadingSubmit) return
      if (!this.$refs.form.validate()) return
      try {
        this.loadingSubmit = true
        await this.$store.dispatch(
          'updateSequencesSchedule', {
            appId: this.$store.getters.appUuid,
            scheduleId: this.selectedScheduleId,
            data: this.schedule
          }
        )
        this.loadingSubmit = false
        notifications.success(this.$t('general.scheduleSuccessfullyUpdated'))
        this.getSchedules()
        this.toggleModal()
      } catch (e) {
        this.loadingSubmit = false
        notifications.error(e)
      }
    }
  },
  props: {
    method: {
      type: String,
      default: 'create'
    },
    selectedScheduleId: {
      type: String,
      default: ''
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    value (value) {
      if (value && this.method === 'edit') {
        this.getSchedule()
      }
    }
  }
}
</script>
